import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
// @ts-expect-error - Swiper CSS 모듈은 타입 정의가 없습니다
import "swiper/css";
// @ts-expect-error - Swiper Pagination CSS 모듈은 타입 정의가 없습니다
import "swiper/css/pagination";

import useHome from "^/customer/home/useHome";
import { useKakaoMap } from "^/common/useKakaoMap";
import { LinkedOwnerBadge } from "@/LinkedOwnerBadge";
import { TermsNoticeBanner } from "@/common/TermsNoticeBanner";
import { useDragScroll } from "^/common/useDragScroll";
import { ensureBannerFont } from "%/bannerFonts";
import {
  useBannerList,
  useCompanyMapList,
  recordBannerImpressions,
  recordBannerClick,
  type BannerItem,
  type CompanyType,
} from "~/services/customer/home";
import { useUserInfo, useTicketList } from "~/services/customer/myPage";
import { useContractInbox } from "~/services/customer/contractRequest";
import { Dialog } from "@/Dialog";
import {
  useUserUsageHistory,
  type UserUsageHistoryItem,
} from "~/services/customer/usageHistory";
import type { PetDataType } from "~/types/common";
import type { TicketListItem } from "~/types/common";

import { IconSearch } from "@/Icon/Search";
import { IconSchool } from "@/Icon/School";
import { IconMungSmall } from "@/Icon/MungSmall";
import { requestDeviceLocation } from "%/nativeLocation";

export const Route = createFileRoute("/customer/layout/home")({
  component: Home,
});

// 최근 이용한 매장 조회 기간 (최근 3개월)
const RECENT_MONTHS = 3;
const RECENT_STORE_LIMIT = 5;

// 위치 권한 거부/실패 시 미니 지도 기본 중심 (서울시청)
const DEFAULT_CENTER = { lat: 37.5665, lng: 126.978 };

function toDateParam(d: Date) {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function formatDate(d?: string) {
  if (!d) return "";
  const only = d.split(/[T ]/)[0]; // 시간 부분 제거 ("2026-07-05T10:00" → "2026-07-05")
  return only.replace(/-/g, ".");
}

// "2026-07-05" → "26.07.05"
function formatShortDate(d?: string) {
  const full = formatDate(d);
  return full.length === 10 ? full.slice(2) : full;
}

/**
 * AI 배너 오버레이 — 히어로 이미지 위에 매장명/지역 문구를 얹어 렌더.
 * CDN CORS 불가로 이미지 합성 대신 표시-시점에 텍스트를 오버레이한다(관리자 미리보기와 동일 레이아웃).
 */
function BannerOverlay({ banner }: { banner: BannerItem }) {
  const zone = banner.textZone || "bottom-left";
  const isTop = zone.includes("top");
  const isRight = zone.includes("right");
  const point = banner.pointColor || "#FF9934";
  const fontFamily = ensureBannerFont(banner.fontKey);
  return (
    <>
      <div
        className={`pointer-events-none absolute inset-0 ${
          isTop ? "bg-gradient-to-b" : "bg-gradient-to-t"
        } from-black/70 via-black/25 to-transparent`}
      />
      <div
        className={`pointer-events-none absolute inset-0 flex flex-col gap-1 p-4 ${
          isTop ? "justify-start" : "justify-end"
        } ${isRight ? "items-end text-right" : "items-start text-left"}`}
      >
        <span
          className="mb-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
          style={{ backgroundColor: point }}
        >
          우리 동네 매장
        </span>
        {banner.headline && (
          <span
            className="line-clamp-2 text-lg font-extrabold leading-tight text-white drop-shadow-md"
            style={{ fontFamily }}
          >
            {banner.headline}
          </span>
        )}
        {banner.subline && (
          <span
            className="line-clamp-1 text-xs font-medium text-white/90 drop-shadow"
            style={{ fontFamily }}
          >
            {banner.subline}
          </span>
        )}
      </div>
    </>
  );
}

function Home() {
  const { history } = useRouter();
  const { petList, selectedPetSeq, handlePetSelect } = useHome();
  const familyScrollRef = useDragScroll<HTMLDivElement>();
  const { data: userInfo } = useUserInfo();
  const { data: ticketList } = useTicketList(true);
  const [searchQuery, setSearchQuery] = useState("");

  // ─── 서명 요청 도착(미서명 계약) 인지 ───
  // SENT/OPENED = 아직 서명 안 한 계약. 홈 상단 카드(뱃지 N) + 최초 1회 팝업으로 인지시킴.
  const { data: contractInbox = [] } = useContractInbox([
    "REQUEST_STATUS_SENT",
    "REQUEST_STATUS_OPENED",
  ]);
  const pendingContracts = contractInbox;
  const pendingCount = pendingContracts.length;
  const latestPending = pendingContracts[0];

  const CONTRACT_SEEN_KEY = "customer-contract-arrival-seen";
  const [showContractPopup, setShowContractPopup] = useState(false);
  // '본 것' 기록은 효과가 아니라 사용자가 닫을 때 한다.
  // (효과에서 즉시 기록하면 StrictMode 이중 마운트로 첫 실행이 기록→둘째 실행에서 팝업 억제됨)
  const markContractSeen = () => {
    try {
      localStorage.setItem(
        CONTRACT_SEEN_KEY,
        JSON.stringify(pendingContracts.map((c) => c.requestSeq)),
      );
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    if (pendingCount === 0) return;
    let seen: number[] = [];
    try {
      seen = JSON.parse(localStorage.getItem(CONTRACT_SEEN_KEY) || "[]");
    } catch {
      seen = [];
    }
    // 아직 팝업으로 안 본 계약이 하나라도 있으면 노출(기록은 닫을 때)
    const hasNew = pendingContracts.some((c) => !seen.includes(c.requestSeq));
    if (hasNew) setShowContractPopup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount]);

  const closeContractPopup = () => {
    markContractSeen();
    setShowContractPopup(false);
  };

  const goContractInbox = () => history.push("/customer/layout/contract-inbox");
  const goSignLatest = () => {
    markContractSeen();
    setShowContractPopup(false);
    if (latestPending) {
      history.push(
        `/customer/layout/contract-sign?requestSeq=${latestPending.requestSeq}` as any,
      );
    } else {
      goContractInbox();
    }
  };

  const handleBannerClick = (banner: BannerItem) => {
    // 효과 집계: 링크 유무와 무관하게 탭은 클릭으로 기록
    recordBannerClick(banner.bannerSeq);
    if (!banner.linkUrl) return;
    if (banner.linkType === "INTERNAL") {
      history.push(banner.linkUrl);
    } else if (banner.linkType === "EXTERNAL") {
      window.open(banner.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  // ─── 내 주변 매장 미니 지도 ───
  const { ready: mapReady } = useKakaoMap();
  const miniMapRef = useRef<HTMLDivElement>(null);
  const miniMapInstanceRef = useRef<any>(null);
  const miniMarkersRef = useRef<any[]>([]);
  const miniLocationOverlayRef = useRef<any>(null);
  // undefined = 위치 조회 중, null = 실패/거부(기본 좌표 사용)
  const [geoCenter, setGeoCenter] = useState<
    { lat: number; lng: number } | null | undefined
  >(undefined);
  // 위치기반 배너: 디바이스 위치가 잡히면 그 좌표로 배너 재조회(연결매장 반경 내 배너 포함)
  const { data: banners } = useBannerList(geoCenter?.lat, geoCenter?.lng);

  // 배너 노출 기록(효과 리포트) — 같은 배너는 세션 내 1회만
  const recordedBannerSeqs = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!banners || banners.length === 0) return;
    const fresh = banners
      .map((b) => b.bannerSeq)
      .filter((seq) => !recordedBannerSeqs.current.has(seq));
    if (fresh.length === 0) return;
    fresh.forEach((seq) => recordedBannerSeqs.current.add(seq));
    recordBannerImpressions(fresh);
  }, [banners]);
  const [miniBounds, setMiniBounds] = useState<{
    swLat: number;
    swLng: number;
    neLat: number;
    neLng: number;
  } | null>(null);

  useEffect(() => {
    let canceled = false;
    // 홈 첫 진입 시 위치 권한을 요청한다(승인 시 현재 위치 기준으로 주변/위치배너 표시).
    // 거부/미승인 시에는 DEFAULT_CENTER(서울시청)로 폴백.
    requestDeviceLocation({ interactive: true }).then(({ location }) => {
      if (canceled) return;
      setGeoCenter(location ? { lat: location.lat, lng: location.lng } : null);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const miniCenter = geoCenter ?? DEFAULT_CENTER;

  useEffect(() => {
    if (!mapReady || !miniMapRef.current || miniMapInstanceRef.current) return;
    const kakao = window.kakao;
    const map = new kakao.maps.Map(miniMapRef.current, {
      center: new kakao.maps.LatLng(miniCenter.lat, miniCenter.lng),
      level: 5,
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
    });
    miniMapInstanceRef.current = map;

    // 내 위치 표시 (위치 조회 성공 시에만)
    if (geoCenter) {
      const dot = document.createElement("div");
      dot.className = "h-4 w-4 rounded-full border-2 border-white shadow";
      dot.style.backgroundColor = "#4A90E2";
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(geoCenter.lat, geoCenter.lng),
        content: dot,
      });
      overlay.setMap(map);
      miniLocationOverlayRef.current = overlay;
    }

    const b = map.getBounds();
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    setMiniBounds({
      swLat: sw.getLat(),
      swLng: sw.getLng(),
      neLat: ne.getLat(),
      neLng: ne.getLng(),
    });
  }, [mapReady, miniCenter, geoCenter]);

  useEffect(() => {
    const map = miniMapInstanceRef.current;
    if (!map || !mapReady || !geoCenter) return;
    const kakao = window.kakao;
    const position = new kakao.maps.LatLng(geoCenter.lat, geoCenter.lng);

    map.setCenter(position);

    if (miniLocationOverlayRef.current) {
      miniLocationOverlayRef.current.setMap(null);
      miniLocationOverlayRef.current = null;
    }

    const dot = document.createElement("div");
    dot.className = "h-4 w-4 rounded-full border-2 border-white shadow";
    dot.style.backgroundColor = "#4A90E2";
    const overlay = new kakao.maps.CustomOverlay({
      position,
      content: dot,
    });
    overlay.setMap(map);
    miniLocationOverlayRef.current = overlay;

    const b = map.getBounds();
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    setMiniBounds({
      swLat: sw.getLat(),
      swLng: sw.getLng(),
      neLat: ne.getLat(),
      neLng: ne.getLng(),
    });
  }, [mapReady, geoCenter]);

  const { data: nearbyCompanies } = useCompanyMapList(
    {
      swLat: miniBounds?.swLat ?? 0,
      swLng: miniBounds?.swLng ?? 0,
      neLat: miniBounds?.neLat ?? 0,
      neLng: miniBounds?.neLng ?? 0,
      category: null,
    },
    !!miniBounds,
  );

  useEffect(() => {
    const map = miniMapInstanceRef.current;
    if (!map || !nearbyCompanies) return;
    const kakao = window.kakao;
    miniMarkersRef.current.forEach((m) => m.setMap(null));
    miniMarkersRef.current = [];
    nearbyCompanies.forEach((c: CompanyType) => {
      // 좌표 없는 매장은 미니 지도에선 생략 (지오코딩은 지도 화면에서만)
      if (typeof c.latitude !== "number" || typeof c.longitude !== "number")
        return;
      const el = document.createElement("div");
      el.className = "h-3 w-3 rounded-full border-2 border-white shadow";
      el.style.backgroundColor = "#FF9934";
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(c.latitude, c.longitude),
        content: el,
      });
      overlay.setMap(map);
      miniMarkersRef.current.push(overlay);
    });
  }, [nearbyCompanies]);

  // 최근 이용한 매장: 최근 3개월 이용내역에서 매장 단위로 중복 제거 (내역은 최신순 정렬)
  const usagePeriod = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - RECENT_MONTHS);
    return { startDate: toDateParam(start), endDate: toDateParam(end) };
  }, []);
  const { data: usageHistory } = useUserUsageHistory(usagePeriod);

  const recentStores = useMemo<UserUsageHistoryItem[]>(() => {
    const items = usageHistory?.data ?? [];
    const seen = new Set<string>();
    const stores: UserUsageHistoryItem[] = [];
    for (const item of items) {
      const key = item.companySeq?.toString() ?? item.companyName ?? "";
      if (!key || seen.has(key)) continue;
      seen.add(key);
      stores.push(item);
      if (stores.length >= RECENT_STORE_LIMIT) break;
    }
    return stores;
  }, [usageHistory]);

  // 보유 이용권: 선택된 반려동물의 이용권만 (반려동물 미지정 이용권은 공용으로 항상 노출)
  const ownedTickets = useMemo<TicketListItem[]>(() => {
    if (!ticketList) return [];
    return ticketList
      .filter(
        (t: TicketListItem) =>
          !t.petSeq || t.petSeq.toString() === selectedPetSeq,
      )
      .sort(
        (a: TicketListItem, b: TicketListItem) =>
          new Date(a.expiredDate).getTime() - new Date(b.expiredDate).getTime(),
      );
  }, [ticketList, selectedPetSeq]);

  const nickname = userInfo?.nickname ?? "보호자";
  const profileChar = nickname.slice(0, 1);

  const onSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    history.push(
      q
        ? `/customer/layout/search?q=${encodeURIComponent(q)}`
        : "/customer/layout/search",
    );
  };

  return (
    <div className="flex-auto bg-[#F5F5F5]">
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between bg-white px-4">
        <div className="flex items-center gap-1.5">
          <IconMungSmall />
          <span className="text-base font-bold">멍투데이</span>
        </div>
        <div className="flex items-center gap-2">
          {/* MY코드 — 오프라인 발급/QR·범용 식별자라 이용권 섹션이 아닌 상단에 상시 노출 */}
          <button
            type="button"
            onClick={() => history.push("/customer/layout/member-code")}
            className="flex items-center gap-1 rounded-full bg-[#FFF3E5] px-3 py-1.5 text-xs font-semibold text-[#FF9934] active:bg-[#ffe9cc]"
            aria-label="MY코드"
          >
            📷 MY코드
          </button>
          <button
            onClick={() => history.push("/customer/layout/user")}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow text-xs font-bold text-white"
            aria-label="마이페이지"
          >
            {profileChar}
          </button>
        </div>
      </header>

      <div className="space-y-5 px-4 pb-28 pt-4">
        {/* 약관 개정 고지 — 고지 기간에만 노출 */}
        <TermsNoticeBanner targetRole="GUARDIAN" />

        {pendingCount > 0 && (
          <button
            type="button"
            onClick={goContractInbox}
            className="flex w-full items-center gap-3 rounded-2xl border border-[#FFD9A3] bg-[#FFF7E6] px-4 py-3 text-left active:bg-[#ffefd6]"
          >
            <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF9934] text-lg">
              ✍️
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F24747] px-1 text-[11px] font-bold text-white">
                {pendingCount}
              </span>
            </span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#111]">
                서명할 계약서가 도착했어요
              </span>
              <span className="mt-0.5 block truncate text-xs text-[#8a6d3b]">
                {latestPending?.companyName ?? ""}
                {latestPending?.contractName
                  ? ` · ${latestPending.contractName}`
                  : ""}
                {pendingCount > 1 ? ` 외 ${pendingCount - 1}건` : ""}
              </span>
            </span>
            <span className="shrink-0 text-[#FF9934]">›</span>
          </button>
        )}
        <div>
          <h1 className="mb-3 text-lg font-bold">안녕하세요, {nickname}님!</h1>
          <form onSubmit={onSubmitSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="매장이름으로 검색"
              className="h-11 w-full rounded-full bg-white px-4 pr-11 text-sm outline-none placeholder:text-gray"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2"
              aria-label="검색"
            >
              <IconSearch />
            </button>
          </form>
        </div>

        {banners && banners.length > 0 && (
          <div className="overflow-hidden rounded-2xl">
            {/* 슬라이드 1장일 때는 loop/autoplay 를 꺼야 함 (Swiper 경고 + 오동작 방지) */}
            <Swiper
              modules={[Autoplay, Pagination]}
              autoplay={
                banners.length > 1
                  ? { delay: 4000, disableOnInteraction: false }
                  : false
              }
              pagination={banners.length > 1 ? { clickable: true } : false}
              loop={banners.length > 1}
              className="h-44"
            >
              {banners.map((banner, i) => (
                <SwiperSlide key={banner.bannerSeq}>
                  <button
                    type="button"
                    onClick={() => handleBannerClick(banner)}
                    disabled={banner.linkType === "NONE" || !banner.linkUrl}
                    className="relative block h-44 w-full"
                    aria-label={banner.title ?? "배너"}
                  >
                    {banner.fileUrl ? (
                      <img
                        src={banner.fileUrl}
                        alt={banner.title ?? ""}
                        className="h-full w-full object-cover"
                        loading={i === 0 ? "eager" : "lazy"}
                      />
                    ) : (
                      // 텍스트-only AI 배너: 주조색 그라디언트 배경
                      <div
                        className="h-full w-full"
                        style={{
                          background: `linear-gradient(135deg, ${
                            banner.pointColor || "#FF9934"
                          } 0%, #1f2937 100%)`,
                        }}
                      />
                    )}
                    {/* AI 오버레이: 이미지/배경 위에 매장명/지역 문구를 얹어 렌더 */}
                    {banner.overlay && (banner.headline || banner.subline) && (
                      <BannerOverlay banner={banner} />
                    )}
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <section>
          <h2 className="mb-2 text-sm font-bold">내 주변 매장</h2>
          {/* 현재 위치 기반 미니 지도 — 탭하면 지도 화면으로 이동 */}
          <div className="relative h-44 overflow-hidden rounded-2xl bg-white">
            <div ref={miniMapRef} className="absolute inset-0" />
            {!mapReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-[#EFEFEF] text-sm text-gray">
                지도 로딩 중...
              </div>
            )}
            <button
              type="button"
              onClick={() => history.push("/customer/layout/map")}
              className="absolute inset-0 z-10"
              aria-label="지도로 한번에 보기"
            />
            {nearbyCompanies && nearbyCompanies.length > 0 && (
              <div className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold shadow">
                주변 {nearbyCompanies.length}곳
              </div>
            )}
            <div className="pointer-events-none absolute bottom-3 right-3 z-10 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold shadow">
              지도로 한번에 보기 →
            </div>
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-bold">우리 가족 정보</h2>
          <div
            ref={familyScrollRef}
            className="flex cursor-grab gap-2 overflow-x-auto pb-1 active:cursor-grabbing [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {petList?.map((pet: PetDataType) => {
              const isSelected = pet.petSeq.toString() === selectedPetSeq;
              return (
                <button
                  key={pet.petSeq}
                  onClick={() =>
                    handlePetSelect({
                      value: pet.petSeq.toString(),
                      label: pet.petName,
                    })
                  }
                  className={`flex min-w-[160px] items-center gap-2 rounded-xl bg-white p-2 pr-4 ${
                    isSelected
                      ? "border-2 border-yellow"
                      : "border-2 border-transparent"
                  }`}
                >
                  <div className="relative shrink-0">
                    <div
                      className={`h-12 w-12 overflow-hidden rounded-full bg-gray-100 ${
                        pet.linkedYn === "Y" ? "ring-2 ring-[#1D9E75]" : ""
                      }`}
                    >
                      {pet.fileUrl ? (
                        <img
                          src={pet.fileUrl}
                          alt={pet.petName}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    {pet.linkedYn === "Y" && (
                      <LinkedOwnerBadge
                        name={pet.linkedOwnerName}
                        imageUrl={pet.linkedOwnerImageUrl}
                        size={18}
                      />
                    )}
                  </div>
                  <div className="min-w-0 text-left">
                    <span className="truncate text-sm font-bold">
                      {pet.petName}
                    </span>
                    <div className="truncate text-xs text-gray">
                      {pet.petBreedName ?? "믹스견"}
                      {pet.petWeight ? ` ${pet.petWeight}kg` : ""}
                    </div>
                  </div>
                </button>
              );
            })}
            <button
              onClick={() => history.push("/customer/layout/user-pet-info-add")}
              className="flex min-w-[140px] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#E5E5E5] bg-white"
            >
              <span className="text-lg text-gray">+</span>
              <span className="text-xs text-gray">반려견 등록</span>
            </button>
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-sm font-bold">최근 이용한 매장</h2>
          {recentStores.length > 0 ? (
            <div className="flex flex-col gap-2">
              {recentStores.map((item) => (
                <button
                  key={item.companySeq ?? item.companyName}
                  onClick={() => {
                    if (!item.companySeq) return;
                    history.push(
                      `/customer/layout/home-serach-detail?companySeq=${item.companySeq}`,
                    );
                  }}
                  className="flex items-center gap-3 rounded-xl bg-white p-3 text-left"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3E5]">
                    <IconSchool active />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">
                      {item.companyName ?? ""}
                    </div>
                    <div className="truncate text-xs text-gray">
                      {item.productName ?? ""}
                    </div>
                  </div>
                  <div className="text-xs text-gray">
                    {formatShortDate(item.dtReservStart ?? undefined)}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-xl bg-white p-4 text-center text-sm text-gray">
              최근 이용한 매장이 없습니다.
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold">보유 이용권</h2>
          </div>
          {ownedTickets.length > 0 ? (
            <div className="flex flex-col gap-2">
              {ownedTickets.map((t) => (
                <button
                  key={t.purchaseTicketSeq}
                  onClick={() =>
                    history.push(
                      `/customer/layout/user-subscription-detail?subscriptionSeq=${t.purchaseTicketSeq}` +
                        // 예약하기로 이어질 때 쓸 반려견 컨텍스트(공용 이용권 대응)
                        (selectedPetSeq ? `&petSeq=${selectedPetSeq}` : ""),
                    )
                  }
                  className="flex items-center gap-3 rounded-xl bg-white p-3 text-left"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3E5]">
                    <IconSchool active />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold">
                      {t.companyName}
                    </div>
                    <div className="truncate text-xs text-gray">{t.name}</div>
                    <div className="truncate text-[11px] text-gray">
                      {formatDate(t.expiredDate)} 까지
                    </div>
                  </div>
                  <div className="text-xs font-bold text-yellow">
                    {t.remainCount} / {t.totalCount}개 남음
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-xl bg-white p-4 text-center text-sm text-gray">
              보유 중인 이용권이 없습니다.
            </div>
          )}
        </section>
      </div>

      {/* 홈 진입 시 계약 도착 1회 팝업 (localStorage 로 재노출 방지) */}
      <Dialog
        visible={showContractPopup && pendingCount > 0}
        persist={false}
        onClose={closeContractPopup}
      >
        <div className="p-1 text-center">
          <div className="mb-2 text-3xl">✍️</div>
          <div className="text-base font-bold text-[#111]">
            서명할 계약서가 도착했어요
          </div>
          <div className="mt-1 text-sm text-gray">
            {latestPending?.companyName ?? ""}
            {latestPending?.contractName
              ? ` · ${latestPending.contractName}`
              : ""}
            {pendingCount > 1 ? ` 외 ${pendingCount - 1}건` : ""}
          </div>
          {latestPending?.dtExpired && (
            <div className="mt-1 text-xs text-[#F24747]">
              ⏰ {latestPending.dtExpired.replace("T", " ").slice(0, 16)} 까지
              서명해 주세요
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={closeContractPopup}
              className="flex-1 rounded-full border border-[#E9E9E9] py-2.5 text-sm text-gray"
            >
              나중에
            </button>
            <button
              type="button"
              onClick={goSignLatest}
              className="flex-[2] rounded-full bg-[#FF9934] py-2.5 text-sm font-bold text-white"
            >
              서명하러 가기
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
